<template>
	<div class="responsive-image" :class="{'responsive-image--zoomed': props.zoom, 'responsive-image--cover': props.cover}" :style="wrapperStyle">
		<img class="responsive-img h-full w-full"
			 :class="[roundableString]"
			 :style="{filter: props.blur ? `blur(${props.blur}px)` : null, 'pointer-events': props.preventDownload ? 'none' : null}"
			 :alt="altText"
			 :loading="props.immediate ? null : 'lazy'"
			 :fetchpriority="props.immediate ? 'high' : undefined"
			 :width="imageWidth"
			 :height="imageHeight"
			 :src="imageSrc"
			 :srcset="imageSrcSet"
			 :sizes="sizes"/>
		<div v-if="props.gradient" class="absolute left-0 top-0 w-full h-full" :style="{backgroundImage: `linear-gradient(${props.gradient})`}"/>
		<slot/>
	</div>
</template>

<script setup lang="ts">
import {useNuxtApp, useHead} from '#app';
import {ref, watch, computed} from 'vue';
import {formatDimension} from "@spoferan/spoferan-ts-core";
import type {ImageFormatOptions} from "@spoferan/spoferan-ts-core";
import {Breakpoints} from "../../config";
import {useRoundable} from "../../composables/useRoundable";
import {stripHtml} from "@spoferan/spoferan-ts-core";
import type {RoundableProps} from "../../composables/useRoundable";
import type {ImageProps, ImageSizeProp} from "../../types/image";
import type { Ref } from 'vue'
const {$image} = useNuxtApp()

type CdnImageProps = {

	// The responsive dimension data of the image it will be displayed
	size?: ImageSizeProp,

	// The background color to use (or true for a slight gray) when the contained image does not fit the aspect ratio
	backgroundColor?: boolean,

	// Whether pointer events are disabled, so that the 'save as' action will not appear
	preventDownload?: boolean,
} & ImageProps & RoundableProps;

const props = defineProps<CdnImageProps>();

const {roundableString} = useRoundable(props);

const imageSrc: Ref<string> = ref('');
const imageSrcSet: Ref<string|null> = ref(null);
const sizes: Ref<string|null> = ref(null);
const imageWidth: Ref<number|null> = ref(null);
const imageHeight: Ref<number|null> = ref(null);
const wrapperStyle = ref({});

const altText = computed(() => props.alt ? stripHtml(props.alt) : null);

const refreshImage = () => {
	const imagePath = typeof props.src === 'string' ? props.src : props.src?.path;
	const isCustomizableImagePath = !(imagePath?.endsWith('.svg') || imagePath?.endsWith('.gif'));

	const requiresResponsiveSizes = !!props.size;
	const imageRatio: number = props.ratio ?? (props.size?.ratio ?? (props.width && props.height ? props.width / props.height : 1));

	let singleWidthStyle: string|null = null;

	// Defines the responsive image dimensions and display style for each max screen width
	if (requiresResponsiveSizes) {
		const imageDimensions: {imageWidth: number, imageHeight: number, maxScreenWidth: number, imageWidthStyle: string}[] = [];
		Object.keys(Breakpoints).forEach((breakpoint, index) => {
			// If the props are defined on the highest level, i.e., without a specific breakpoint, we use them as default size specification for the smallest breakpoint
			const breakpointProps = props.size![breakpoint] ?? (index === 0 ? props.size : null);
			if (breakpointProps && (isCustomizableImagePath || index === 0)) {
				const breakpointScreenWidth: number = Breakpoints[breakpoint];
				const propsWidth: number | string = breakpointProps.width ?? Math.round(breakpointProps.height * imageRatio);
				const isRelativeWidth: boolean = typeof propsWidth === "string";
				const propsStringWidth: string = (propsWidth as string);
				const propsNumericWidth: number = isRelativeWidth ? parseFloat(propsStringWidth.substring(0, propsStringWidth.length - (propsStringWidth.endsWith('%') ? 1 : 2))) : (propsWidth as number);
				const propsPixelWidth: number = isRelativeWidth ? (breakpointScreenWidth * (propsNumericWidth / 100)) : propsNumericWidth;
				const propsPixelHeight: number = breakpointProps.height ?? Math.round(propsPixelWidth / imageRatio);

				imageDimensions.push({
					imageWidth: Math.round(propsPixelWidth),
					imageHeight: Math.round(propsPixelHeight),
					maxScreenWidth: breakpointScreenWidth,
					imageWidthStyle: formatDimension(propsWidth)
				});
			}
		});

		// We use '1x' and '2x' dimensions for each specified size to support devices with a higher dpr
		const devicePixelRatios = [1, 2];
		const imageSrcSetParts = [];
		imageDimensions.forEach((imageDimension) => {
			devicePixelRatios.forEach(devicePixelRatio => {
				const imageUrl = $image.format(props.src, {
					width: Math.round(imageDimension.imageWidth * devicePixelRatio),
					height: Math.round(imageDimension.imageHeight * devicePixelRatio),
					fit: props.cover ? 'cover' : 'contain',
				} as ImageFormatOptions);

				imageSrcSetParts.push(`${imageUrl} ${imageDimension.imageWidth * devicePixelRatio}w`);
			});
		});

		// Remove imageSrcSet parts that are duplicates which might occur due to dpr calculations
		const uniqueSrcSetParts = [...new Set(imageSrcSetParts)];

		// The fallback source is the source with the highest definition, i.e., the last one
		imageSrc.value = uniqueSrcSetParts[uniqueSrcSetParts.length - 1].split(' ')[0];

		if (imageDimensions.length === 1) {
			imageWidth.value = imageDimensions[0].imageWidth;
			imageHeight.value = imageDimensions[0].imageHeight;
			singleWidthStyle = imageDimensions[0].imageWidthStyle;

			if (props.immediate && process.server) {
				useHead({
					link: [{
						rel: 'preload',
						as: 'image',
						href: imageSrc.value
					}]
				})
			}
		} else {
			sizes.value = imageDimensions.map((imageDimension, index) => {
				// The last size is not limited
				if (index < imageDimensions.length - 1) {
					return `(max-width: ${imageDimension.maxScreenWidth}px) ${imageDimension.imageWidthStyle}`;
				}

				return imageDimension.imageWidthStyle;
			}).join(', ');
			imageSrcSet.value = uniqueSrcSetParts.join(', ');

			imageWidth.value = imageDimensions[imageDimensions.length - 1].imageWidth;
			imageHeight.value = imageDimensions[imageDimensions.length - 1].imageHeight;

			if (props.immediate && process.server) {
				useHead({
					link: [{
						rel: 'preload',
						as: 'image',
						href: imageSrc.value,
						imagesizes: sizes.value,
						imagesrcset: imageSrcSet.value
					}]
				})
			}
		}
	} else {
		imageWidth.value = props.width ?? (props.height ? Math.round(props.height * imageRatio) : null);
		imageHeight.value = props.height ?? (props.width ? Math.round(props.width / imageRatio) : null);

		const devicePixelRatio = 2;
		imageSrc.value = $image.format(props.src, {
			width: imageWidth.value ? Math.round(imageWidth.value * devicePixelRatio) : null,
			height: imageHeight.value ? Math.round(imageHeight.value * devicePixelRatio) : null,
			fit: props.cover ? 'cover' : 'contain',
		} as ImageFormatOptions);

		singleWidthStyle = imageWidth.value ? formatDimension(imageWidth.value) : null;

		if (props.immediate && process.server) {
			useHead({
				link: [{
					rel: 'preload',
					as: 'image',
					href: imageSrc.value
				}]
			})
		}
	}

	wrapperStyle.value = {
		maxWidth: '100%',
		width: singleWidthStyle === '100vw' ? '100%' : singleWidthStyle,
		// Do not add min width for 'this.currentDimensions.width' as it might overflow max width
	}
}

refreshImage();

watch(() => props.src, () => {
	refreshImage();
});
watch(() => props.size, () => {
	refreshImage();
});

</script>

<style>
.responsive-image {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}
.responsive-image .responsive-img {
  border-radius: inherit;
  transition: opacity 0.8s, transform 0.4s;
  opacity: 1;
}
.responsive-image .responsive-img.loading {
  opacity: 0;
}
.responsive-image.responsive-image--zoomed .responsive-img {
  transform: scale(1.05);
}
.responsive-image.responsive-image--cover {
  width: 100%;
}
.responsive-image.responsive-image--cover .responsive-img {
  width: 100%;
  object-fit: cover;
}
</style>